/* eslint-disable jsx-a11y/label-has-associated-control */
import { Capacitor } from '@capacitor/core'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useLoginMessage } from '@apps-common/apis/auth'
import { FormContainer } from '@apps-common/apps/portal/pages/authenticate/components/FormContainer'
import { ButtonRoundedSm, InputSM } from '@apps-common/components/elements'
import { VertSpacerMd } from '@apps-common/components/layout'
import { AUTHENTICATE_OPTION_FORGOT_PASS } from '@apps-common/config/authenticate-options'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { EMAIL_VALIDATION_REGEX } from '@apps-common/config/regex'
import { useAuth } from '@apps-common/contexts'
import { useAuthenticateScreenStore } from '@apps-common/stores/useAuthenticateScreenStore'
import { getSafeRedirectPath } from '@apps-common/utils/auth-redirect'
import { useIonRouter } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'
import { TfiInfoAlt } from 'react-icons/tfi'

type FormInputs = {
  email: string
  password: string
  remember: boolean
}

interface IProps {
  isMobile: boolean
}

export const LoginForm: React.FC<IProps> = ({ isMobile }) => {
  const isNative = Capacitor.isNativePlatform()
  const router = useIonRouter()
  const { setScreen } = useAuthenticateScreenStore()
  const { logIn } = useAuth()

  const { data: loginMessageData } = useLoginMessage()

  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [loginError, setLoginError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<FormInputs>()

  const emailVal = watch('email')
  const passwordVal = watch('password')

  useEffect(() => {
    if (loginError) {
      setLoginError(null)
    }
  }, [emailVal, passwordVal])

  const onSubmit = async (inputs: FormInputs) => {
    const res = await logIn(inputs.email, inputs.password, true)

    if (!res || typeof res === 'string') {
      console.log(res)
      setLoginError((res as string) || 'Incorrect Email/Password combination')
      return
    }

    router.push(getSafeRedirectPath(window.location.search, '/sites'), 'root', 'replace')
  }

  // const onBiometricSuccess = (username: string, password: string) => {
  //   console.log(`Biometric success: ${username}, ${password}`)
  // }

  // const bio = useBiometrics({ onSuccess: onBiometricSuccess })

  const handleOnShowPassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <FormContainer isMobile={isMobile} className='max-w-[450px] sm:!min-w-[450px]'>
      <div className='absolute top-0 left-0 text-on-mm-brand bg-gradient-to-br from-mm-brand to-mm-brand-2 w-full flex items-center justify-center p-2'>
        <div className='flex items-center justify-center'>
          <h2 className='text-[1rem] jost-medium'>Welcome</h2>
        </div>
      </div>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        {!loginError && loginMessageData?.message && (
          <motion.div key='login-message-card' {...motionFadeIn(0.25)} className='bg-surface-2 !text-text/70 rounded-3xl px-5 py-3 mb-5'>
            {loginMessageData?.title && (
              <div className='jost-medium text-[0.9rem] mb-[7px] flex items-center justify-start gap-2'>
                <TfiInfoAlt className='text-[1rem]' />
                {loginMessageData?.title}
              </div>
            )}
            <div className='jost text-[0.80rem] opacity-80'>{loginMessageData?.message}</div>
          </motion.div>
        )}
        {loginError && (
          <div className='bg-error px-3 py-3 rounded-2xl mb-[20px]'>
            <p className='text-center text-[0.9em] text-on-error'>{loginError}</p>
          </div>
        )}
        <AnimatePresence></AnimatePresence>
        <div className='my-4 flex flex-col'>
          <InputSM
            type='email'
            placeholder='Enter your email'
            label='Email'
            id='email'
            showLabel
            registration={register('email', {
              validate: (value) => {
                if (!value || value.trim() === '') {
                  return 'Required'
                }
                if (!EMAIL_VALIDATION_REGEX.test(value)) {
                  return 'Invalid email address'
                }

                return true
              }
            })}
            disabled={isSubmitting}
            errors={errors.email}
          />
          <InputSM
            type={showPassword ? 'text' : 'password'}
            placeholder='Enter your password'
            label='Password'
            id='password'
            showLabel
            className={classNames(!showPassword && passwordVal?.length > 0 && 'font-[Verdana] tracking-wide')}
            registration={register('password', {
              validate: (value) => {
                if (!value || value.trim() === '') {
                  return 'Required'
                }

                return true
              }
            })}
            disabled={isSubmitting}
            errors={errors.password}
          >
            <button
              type='button'
              className='absolute top-0 right-0 flex items-center h-[37px] px-3 disabled:text-text-muted/70 text-text'
              disabled={isSubmitting}
              onClick={handleOnShowPassword}
            >
              {showPassword && <commonIcons.eyeInvisible className='text-lg' />}
              {!showPassword && <commonIcons.eye className='text-lg' />}
            </button>
          </InputSM>
          {/* {isNative && <div className='text-center'>{bio.component}</div>} */}
          <div className='flex items-center justify-end'>
            <button
              type='button'
              className='text-text/70 hover:opacity-70 disabled:opacity-70 disabled:cursor-not-allowed text-[0.8rem] jost-medium flex items-center justify-end'
              disabled={isSubmitting}
              onClick={() => setScreen(AUTHENTICATE_OPTION_FORGOT_PASS)}
            >
              Forgot password <commonIcons.arrowRightShort className='text-[1.2em]' />
            </button>
          </div>
        </div>
        <VertSpacerMd />
        <ButtonRoundedSm
          type='submit'
          loading={isSubmitting}
          text='Login'
          icon={<commonIcons.arrowAngled />}
          iconRight
          className='w-full jost text-on-mm-brand-2 bg-gradient-to-r from-mm-brand to-mm-brand-2 disabled:opacity-30'
        />
      </form>
    </FormContainer>
  )
}
